import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { createLocation } from '@/api/resources/locations'
import { EditLocationForm } from '@/components/locations/edit-location-form'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { useRouteModal } from '@/hooks/use-route-modal'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/organisations/$organisationId/locations_/new')({
  head: () => ({ meta: [{ title: 'New Location - MyJunea' }] }),
  component: NewLocationRoute,
})

function NewLocationRoute(): React.ReactElement {
  const { organisationId } = Route.useParams()
  const navigate = useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({
        to: '/organisations/$organisationId',
        params: { organisationId },
        resetScroll: false,
      }),
  )
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const organisationIdNum = Number(organisationId)

  const mutation = useMutation({
    mutationFn: async (values: Parameters<typeof createLocation>[0]) => createLocation(values),
    onSuccess: () => {
      clearServerErrors()
      void queryClient.invalidateQueries({ queryKey: queryKeys.locations.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.all })
      void router.invalidate()
      toast.success('Location created')
      handleClose()
    },
    onError: handleMutationError,
  })

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex flex-col sm:max-w-lg">
        <EditLocationForm
          title="New Location"
          submitLabel="Create location"
          onSubmit={(values) => mutation.mutate({ organisation_id: organisationIdNum, ...values })}
          isPending={mutation.isPending}
          serverErrors={serverErrors}
        />
      </SheetContent>
    </Sheet>
  )
}
